import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Check } from "lucide-react";
import { Reveal, PageHero, Eyebrow } from "@/components/Motion";
import { CONTACTS } from "@/data/content";
import { useLanguage } from "@/i18n";

export default function Servizi() {
  const { hash } = useLocation();
  const { t, content } = useLanguage();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
      }
    }
  }, [hash]);

  return (
    <main data-testid="servizi-page">
      <PageHero
        eyebrow={t("serviziHeroEyebrow")}
        lines={[t("serviziL1"), t("serviziL2")]}
        sub={t("serviziSub")}
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32">
        <div className="space-y-24 md:space-y-32">
          {content.services.map((service, i) => (
            <motion.article
              key={service.id}
              id={service.id}
              data-testid={`service-section-${service.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="scroll-mt-28"
            >
              <div
                className={`grid items-center gap-10 md:grid-cols-12 md:gap-14 ${
                  i % 2 === 1 ? "" : ""
                }`}
              >
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <div className="group relative overflow-hidden">
                    <img
                      src={service.img}
                      alt={service.title}
                      className="img-duotone aspect-[8/5] w-full object-cover"
                      loading="lazy"
                    />
                    <span className="absolute left-4 top-4 bg-[#F7F5F0]/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8F6B3C] backdrop-blur-sm">
                      {service.num}
                    </span>
                  </div>
                </div>
                <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <Eyebrow>{t("serviceLabel")} {service.num}</Eyebrow>
                  <h2 className="mt-4 font-serif text-3xl uppercase leading-tight tracking-tight text-stone-900 md:text-4xl">
                    {service.title}
                  </h2>
                  <p className="mt-6 text-base leading-relaxed text-stone-500">{service.desc}</p>
                  <a
                    data-testid={`service-whatsapp-${service.id}`}
                    href={`${CONTACTS.whatsapp}?text=${encodeURIComponent(
                      `${t("waServiceMsg")} ${service.title}.`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 inline-flex items-center gap-3 whitespace-nowrap border border-[#C89D66]/60 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.15em] text-[#8F6B3C] transition-colors duration-300 hover:bg-[#C89D66] hover:text-[#0F1012]"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {t("serviceCta")}
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <Reveal className="mt-24 md:mt-32">
          <div className="border border-stone-300 bg-[#FFFFFF] p-10 md:p-14">
            <div className="grid gap-10 md:grid-cols-2 md:items-center">
              <div>
                <Eyebrow>{t("methodEyebrow")}</Eyebrow>
                <h2 className="mt-4 font-serif text-3xl uppercase leading-tight text-stone-900 md:text-4xl">
                  {t("methodTitleA")} <span className="italic text-[#8F6B3C]">{t("methodTitleB")}</span>
                </h2>
              </div>
              <ul className="space-y-4">
                {[t("method1"), t("method2"), t("method3"), t("method4")].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-stone-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#8F6B3C]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
